import React from 'react';
import theme from 'theme';
import { Text } from 'library';
import { useMediaQuery } from '@mui/material';

import PublicTeacherCard from './PublicTeacherCard';
import FindingTeachersLoader from './FindingTeachersLoader';

import { ITeacher } from 'models/Providers/Teacher';
import NoWorkersFound from './NoWorkersFound';
import SuccessMessage from './SuccessMessage';
import { SeeMoreTeachers } from './SeeMoreTeachers';
import { StaffFilterOptions } from './StaffFilter';
import PublicFilters from './PublicFilters';
import {
    Container,
    InnerContainer,
    TitleRow,
    TwoColumnLayout,
    SidebarColumn,
    StickyFilters,
    Divider,
    MainColumn,
    MobileFiltersRow,
    ResultsContainer,
    TeacherList,
} from './StaffNearYou.styles';
import { Stack } from 'library/ui';

export function StaffNearYou({
    signUp,
    zipCode,
    searchedZipCode,
    updateZipCode,
    onSearch,
    loading,
    workers,
    vettedWorkersCount,
    filters,
}: {
    signUp: () => void;
    zipCode: string;
    searchedZipCode: string;
    updateZipCode: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch: (filters: StaffFilterOptions) => void;
    loading: boolean;
    workers?: ITeacher[];
    vettedWorkersCount: number;
    filters: StaffFilterOptions;
}) {
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const titleAndSuccess = (
        <>
            <TitleRow>
                <Text variant="display" bold>
                    Available Teachers Near {searchedZipCode}
                </Text>
            </TitleRow>
            <SuccessMessage visible={!loading && !!workers && workers.length > 0} />
        </>
    );

    const resultsContent = (
        <>
            {loading && <FindingTeachersLoader zipCode={searchedZipCode} />}
            {!loading && (!workers || workers.length === 0) && <NoWorkersFound signUp={signUp} />}
            <TeacherList style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
                <Stack gap="sm" fullWidth>
                    {!loading &&
                        workers?.slice(0, 10).map((worker) => <PublicTeacherCard key={worker.id} worker={worker} />)}
                </Stack>
            </TeacherList>
            {!loading && workers && workers.length > 0 && (
                <SeeMoreTeachers signUp={signUp} vettedWorkersCount={vettedWorkersCount} />
            )}
        </>
    );

    const filtersContent = (
        <PublicFilters zipCode={zipCode} updateZipCode={updateZipCode} filters={filters} onSearch={onSearch} />
    );

    if (!isDesktop) {
        return (
            <Container>
                <InnerContainer>
                    {titleAndSuccess}
                    <MobileFiltersRow>{filtersContent}</MobileFiltersRow>
                    <ResultsContainer>{resultsContent}</ResultsContainer>
                </InnerContainer>
            </Container>
        );
    }

    return (
        <Container>
            <TwoColumnLayout>
                <SidebarColumn>
                    <StickyFilters>{filtersContent}</StickyFilters>
                </SidebarColumn>
                <Divider />
                <MainColumn>
                    {titleAndSuccess}
                    <ResultsContainer>{resultsContent}</ResultsContainer>
                </MainColumn>
            </TwoColumnLayout>
        </Container>
    );
}
